import React, { useCallback, useEffect, useState } from 'react';
import { Flex, VerticalSpacing } from '@banhala/ui-kit';
import type { InquiryTabProps } from './types/InquiryTabProps';
import { ContentContainer } from '../ContentContainer';
import {
  InquiryGoodsSkuCode,
  InquiryGoodsQuestion,
  InquiryOrderedGoodsInformation,
} from './components';
import type { GoodsContactsOrderItem } from 'shared/src/api';
import { useGoodsSno } from '../../../../hooks/useGoodsSno';
import { isWeb, useNavigator } from 'shared/src/utils';
import { useLogGoodsAnalyticsEvent } from '../../../../hooks/useLogGoodsAnalyticsEvent';
import { useAppDownloadModal } from 'shared/src/hooks';

export const InquiryTab: React.FC<InquiryTabProps> = ({
  focused,
  onChangeTabHeight,
}) => {
  const goodsSno = useGoodsSno();
  const logGoodsDetail = useLogGoodsAnalyticsEvent();
  const [height, setHeight] = useState(0);
  const [savedHeight, setSavedHeight] = useState(0);
  const { navigator } = useNavigator();
  const { show: openModal } = useAppDownloadModal();

  useEffect(() => {
    if (height !== 0 && savedHeight !== height) {
      setSavedHeight(height);
      onChangeTabHeight(height);
    }
  }, [height, onChangeTabHeight, savedHeight]);

  const handleClickNotice = useCallback(() => {
    navigator.moveToWebview(`/goods/${goodsSno}/guide`);
  }, [goodsSno, navigator]);

  // 주문 내역 탭 → 주문 상세로. 웹뷰(ably-webview-react) 와 동일하게 `/orders/{orderSno}` 로 보낸다.
  // 그 경로는 전용 화면(OrderDetail)이 등록돼 있어 주소가 유지된다.
  const handlePressOrderItem = useCallback(
    (orderItem: GoodsContactsOrderItem) => {
      logGoodsDetail({
        name: 'CLICK_WRITE_ORDER_INQUIRY',
        analytics: {
          ORDER_SNO: orderItem.order_sno.toString(),
          ORDERED_AT: orderItem.ordered_at,
        },
      });

      navigator.moveToWebview(`/orders/${orderItem.order_sno}`);
    },
    [logGoodsDetail, navigator],
  );

  const handleClickInquiry = useCallback(() => {
    logGoodsDetail({
      name: 'CLICK_WRITE_GOODS_INQUIRY',
    });

    if (isWeb) {
      openModal();
      return;
    }

    navigator.moveToWebview(`/help/inquiry/new?type=GOODS&sno=${goodsSno}`, {
      titleBar: true,
    });
  }, [logGoodsDetail, navigator, goodsSno, openModal]);

  return (
    <ContentContainer focused={focused}>
      <Flex
        onLayout={event => {
          setHeight(event.nativeEvent.layout.height);
        }}
      >
        <InquiryGoodsSkuCode />
        <InquiryOrderedGoodsInformation
          onPressOrderItem={handlePressOrderItem}
        />
        <InquiryGoodsQuestion
          onClickNotice={handleClickNotice}
          onClickInquiry={handleClickInquiry}
        />
        <VerticalSpacing size={64} />
      </Flex>
    </ContentContainer>
  );
};
